import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { debounce } from 'lodash'
// @ts-ignore
import { Search as SearchIcon, Cross as CrossIcon } from '@netbasequid/core'
// @ts-ignore
import { useSysTheme } from 'hooks/useUiState'
import { SystemTheme } from 'hooks/useUiState/types'
import { setFilterKeyWord, clearFilterKeyWord } from 'features/dashboardInfo/dashboardInfoSlice'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { selectDashboardInfoSortingAndFilter } from 'features/dashboardInfo/selectors'
import { AutomationId } from 'utils/automationUtil'
import { heapTrack, HeapEvent, HeapEventPageValue } from 'utils/heapUtils'

const SearchBarContainer = styled.div<{ theme: SystemTheme }>`
  border: 1px solid ${({ theme }) => theme.interaction.border.default};
  &:focus-within {
    border: 1px solid ${({ theme }) => theme.interaction.primary.default};
  }

  border-radius: 2px;
  padding: 4px 6px;
  display: flex;
  align-items: center;
  height: 32px;
  box-sizing: border-box;
`

const StyledInput = styled.input<{ $theme: SystemTheme }>`
  border: none;
  outline: none;
  margin: 0px 8px;
  flex: 1;
  background-color: transparent;
  color: ${({ $theme }) => $theme.text.placeholder};
  font-size: ${({ $theme }) => $theme.typescale[14].size};
  line-height: ${({ $theme }) => $theme.typescale[14].lineHeight};
`

const IconContainer = styled.div<{ $cursor?: string }>`
  display: flex;
  align-items: center;
  cursor: ${({ $cursor }) => $cursor || 'pointer'};
`
interface SearchBarProps {
  placeholder?: string
  page: HeapEventPageValue
}

const SearchBar = ({ placeholder, page }: SearchBarProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const theme = useSysTheme()
  const dispatch = useAppDispatch()
  const { filter } = useAppSelector(selectDashboardInfoSortingAndFilter)
  const [visible, setVisible] = useState(Boolean(filter.keyword))

  const debouncedInputChange = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setFilterKeyWord(e.target.value))
    heapTrack(HeapEvent.V4SearchKeyword, {
      keyword: e.target.value,
      page
    })
  }, 800)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVisible(Boolean(e.target.value))
    debouncedInputChange(e)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      dispatch(setFilterKeyWord((e.target as HTMLInputElement).value))
    }
  }

  const handleClear = () => {
    if (inputRef.current) {
      inputRef.current.value = ''
      setVisible(false)
      dispatch(clearFilterKeyWord())
    }
  }
  return (
    <SearchBarContainer theme={theme}>
      <IconContainer $cursor="default">
        <SearchIcon color={theme.text.placeholder} />
      </IconContainer>
      <StyledInput
        type="text"
        ref={inputRef}
        $theme={theme}
        data-testid={AutomationId.sidePannel.searchBox}
        placeholder={placeholder}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        defaultValue={filter.keyword}
      />
      {visible && (
        <IconContainer onClick={handleClear}>
          <CrossIcon color={theme.text.placeholder} />
        </IconContainer>
      )}
    </SearchBarContainer>
  )
}

export default SearchBar
